import {
	Checkbox,
	Heading,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputRightElement,
	Progress,
	Td,
	Thead,
	Tr,
	VStack
} from "@chakra-ui/react";
import { cloneDeep, isEmpty } from "lodash";
import React, { FunctionComponent, ReactElement } from "react";
import { CloseIcon, TriangleDownIcon, TriangleUpIcon, UpDownIcon } from "@chakra-ui/icons";

// Types
// import { CustomColumnFilterType, DataTableColumn } from "./../../DataTable/types";
import {
	CustomColumnFilterOption,
	CustomColumnFilterType,
	DataTableColumn,
	KVP,
	SortDirection
} from "../../../utils/types";
import ColumnFilterSingleSelect from "./ColumnFilterSingleSelect";
import ColumnFilterMultiSelect from "./ColumnFilterMultiSelect";
import ColumnFilterDatePicker from "./ColumnFilterDatePicker";
// import MultiSelect, { MultiSelectOption } from "../../MultiSelect/MultiSelect";

interface DataTableHeaderProps {
	columns: Array<DataTableColumn>;
	sortField: string;
	sortDirection: SortDirection;
	searchModel: KVP;
	searchEnabled: boolean;
	selectionEnabled: boolean;
	isLoading?: boolean;

	sortFieldHandler: React.Dispatch<React.SetStateAction<string>>;
	sortDirectionHandler: React.Dispatch<React.SetStateAction<SortDirection>>;
	setSearchModel: React.Dispatch<React.SetStateAction<KVP>>;
	handleBulkSelect: (isChecked: boolean) => void;
}

/**
 * Handles Rendering Table Header Columns as well as sorting and searching on given columns
 * @param props typeof: DataTableHeaderProps
 */
const DataTableHeader: FunctionComponent<DataTableHeaderProps> = (props: DataTableHeaderProps) => {
	const {
		columns,
		sortField,
		sortDirection,
		searchModel,
		searchEnabled,
		selectionEnabled,
		isLoading,
		sortFieldHandler,
		sortDirectionHandler,
		setSearchModel,
		handleBulkSelect
	} = props;

	/**
	 * Determines whether to render the Up, Down, or Indeterminate sort icon
	 * @param columnId ID of the Header Column
	 * @returns <UpDownIcon /> || <TriangleUpIcon /> || <TriangleDownIcon />
	 */
	const getSortIcon = (columnId: string): ReactElement => {
		if (columnId.toLowerCase() !== sortField.toLowerCase()) return <UpDownIcon />;
		switch (sortDirection) {
			default:
			case SortDirection.Asc:
				return <TriangleUpIcon />;
			case SortDirection.Desc:
				return <TriangleDownIcon />;
		}
	};

	const getCustomSearchField = (column: DataTableColumn): ReactElement => {
		if (column.customFilter) {
			switch (column.customFilter.type) {
				default:
				case CustomColumnFilterType.Select:
					if (column.customFilter.options)
						return (
							<ColumnFilterSingleSelect
								isDisabled={isLoading}
								options={column.customFilter.options}
								existingValue={searchModel[column.id]}
								onChangeHandler={(newValue) => handleMultiSearch(column.id, [newValue?.value?.toString() ?? ""])}
							/>
						);
					else return <></>;
				case CustomColumnFilterType.MultiSelect:
					if (column.customFilter.options)
						return (
							<ColumnFilterMultiSelect
								isDisabled={isLoading}
								options={column.customFilter.options}
								existingValues={searchModel[column.id]}
								onChangeHandler={(newValues) =>
									handleMultiSearch(
										column.id,
										newValues.map((value: CustomColumnFilterOption) => value.value.toString())
									)
								}
							/>
						);
					else return <></>;
				case CustomColumnFilterType.DatePicker:
					return (
						<ColumnFilterDatePicker
							isDisabled={isLoading}
							column={column}
							date={searchModel[column.id] as string}
							handleSearch={handleSearch}
						/>
					);
			}
		} else {
			console.error(`Attempting to render custom search field for undefined type: ID - ${column.id}`);
			return <></>;
		}
	};

	/**
	 * Handles the event wherein the user sorts a given column
	 * @param columnId ID of the Header Column
	 */
	const handleSortClick = (columnId: string): void => {
		sortFieldHandler(columnId);
		sortDirectionHandler(sortDirection === SortDirection.Asc ? SortDirection.Desc : SortDirection.Asc);
	};

	/**
	 * Handles the event wherein the user searches on a given column
	 * @param columnId ID of the Header Column
	 * @param value Search Value
	 */
	const handleSearch = (columnId: string, value: string): void => {
		// Clone this so we don't overwrite "immutable" properties
		const tmpSrchModel = cloneDeep(searchModel);
		tmpSrchModel[columnId] = value;
		setSearchModel(tmpSrchModel);
	};

	const handleMultiSearch = (columnId: string, values: Array<string>): void => {
		const tmpSrchModel = cloneDeep(searchModel);
		tmpSrchModel[columnId] = values.join("|");
		setSearchModel(tmpSrchModel);
	};

	return (
		<Thead>
			<Tr>
				{selectionEnabled && (
					<Td
						paddingInlineStart={2}
						paddingInlineEnd={2}
						border="none"
						style={{
							padding: "8px"
						}}>
						<Checkbox
							key="checkbox"
							colorScheme="orange"
							onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleBulkSelect(event.target.checked)}
						/>
					</Td>
				)}
				{columns.map((column) => (
					<Td
						key={column.id}
						fontWeight="black"
						verticalAlign="top"
						border="none"
						display={column.isHidden ? "none" : "table-cell"}
						paddingInlineStart={2}
						paddingInlineEnd={2}
						maxWidth={column.maxWidth}>
						<VStack>
							<HStack justifyContent="space-between" width="100%" minHeight="38px">
								{column.header && (
									<Heading size="sm" style={{ textWrap: "wrap" }}>
										{column.header}
									</Heading>
								)}
								{column.isSortable ? (
									<IconButton
										ml={3}
										size="sm"
										aria-label="sort"
										background="transparent"
										color={column.id.toLowerCase() == sortField.toLowerCase() ? "orange.500" : "inherit"}
										marginLeft={isEmpty(column.header) ? "0px !important" : ""}
										minWidth={5}
										icon={getSortIcon(column.id)}
										isDisabled={isLoading}
										onClick={() => {
											handleSortClick(column.id);
										}}
									/>
								) : null}
							</HStack>
							{column.filterable && searchEnabled ? (
								column.customFilter ? (
									getCustomSearchField(column)
								) : (
									<InputGroup size="md">
										<Input
											size="sm"
											placeholder="⌕"
											pl="7px"
											pr={searchModel[column.id] ? "24px" : "10px"}
											color={"white"}
											background={"white"}
											textColor={"black"}
											borderRadius="4px"
											_hover={{ borderColor: "gray.400" }}
											css={{
												"&::placeholder": {
													textAlign: "right"
												},
												"&::-webKit-input-placeholder": {
													textAlign: "right"
												}
											}}
											value={searchModel[column.id] ?? ""}
											isDisabled={isLoading}
											onChange={(event) => handleSearch(column.id, event.target.value)}
										/>
										{searchModel[column.id] && (
											<InputRightElement h="32px" w="32px">
												<IconButton
													size="xs"
													p="5px"
													h="20px"
													aria-label="sort"
													minWidth="15px"
													icon={<CloseIcon fontSize="9px" />}
													isDisabled={isLoading}
													onClick={() => handleSearch(column.id, "")}></IconButton>
											</InputRightElement>
										)}
									</InputGroup>
								)
							) : null}
						</VStack>
					</Td>
				))}
			</Tr>
			<Tr>
				<Td colSpan={columns.length + 1} padding="0" height="4px">
					{isLoading && <Progress backgroundColor="white" size="xs" colorScheme="orange" isIndeterminate />}
				</Td>
			</Tr>
		</Thead>
	);
};

export default DataTableHeader;
